import { nextTick, ref } from "vue";

const SCROLL_THRESHOLD_UPWARD = 120; // Distance from edge to start scrolling
const SCROLL_THRESHOLD_DOWNWARD = 100;
const SCROLL_SPEED = 10; // Pixels to scroll per interval
const SCROLL_INTERVAL_MS = 16; // ~60fps

/**
 * @description Composable for auto-scrolling during drag operations
 * @param containerId - ID of the scrollable container element (default: "form-builder")
 * @returns Object with handleDragStart and handleDragEnd functions to attach to VueDraggableNext
 */
export function useAutoScroll(containerId = "form-builder") {
  const isDragging = ref(false);
  const scrollInterval = ref<number | null>(null);

  /**
   * @description Clears the scroll interval if it exists
   */
  const clearScrollInterval = () => {
    if (scrollInterval.value !== null) {
      clearInterval(scrollInterval.value);
      scrollInterval.value = null;
    }
  };

  /**
   * @description Creates a scroll interval for the specified direction
   * @param direction - "up" or "down"
   */
  const createScrollInterval = (direction: "up" | "down") => {
    if (scrollInterval.value) {
      return; // Already scrolling
    }

    scrollInterval.value = window.setInterval(() => {
      const container = document.getElementById(containerId);
      if (!container || !isDragging.value) {
        clearScrollInterval();
        return;
      }

      const maxScroll = container.scrollHeight - container.clientHeight;

      if (direction === "up") {
        if (container.scrollTop > 0) {
          container.scrollTop = Math.max(0, container.scrollTop - SCROLL_SPEED);
        } else {
          clearScrollInterval();
        }
      } else {
        // direction === "down"
        if (container.scrollTop < maxScroll) {
          container.scrollTop = Math.min(
            maxScroll,
            container.scrollTop + SCROLL_SPEED
          );
        } else {
          clearScrollInterval();
        }
      }
    }, SCROLL_INTERVAL_MS);
  };

  const startAutoScroll = () => {
    const scrollContainer = document.getElementById(containerId);
    if (!scrollContainer) {
      return;
    }

    // Use 'dragover' on the scroll container instead of 'drag' on document.
    // Firefox reports clientY=0 for all 'drag' events, but 'dragover'
    // fires on the element being dragged over and reports correct clientY
    // in all browsers including Firefox.
    const dragOverHandler = (e: DragEvent) => {
      if (!isDragging.value) {
        clearScrollInterval();
        return;
      }

      const mouseY = e.clientY;
      const viewportHeight = window.innerHeight;

      if (mouseY > 0 && mouseY < SCROLL_THRESHOLD_UPWARD) {
        createScrollInterval("up");
      } else if (mouseY > viewportHeight - SCROLL_THRESHOLD_DOWNWARD) {
        createScrollInterval("down");
      } else {
        clearScrollInterval();
      }
    };

    scrollContainer.addEventListener("dragover", dragOverHandler, {
      passive: true,
    });
    (scrollContainer as any)._autoScrollDragOverHandler = dragOverHandler;
  };

  const stopAutoScroll = () => {
    clearScrollInterval();
    const scrollContainer = document.getElementById(containerId);
    if (scrollContainer) {
      if ((scrollContainer as any)._autoScrollDragOverHandler) {
        scrollContainer.removeEventListener(
          "dragover",
          (scrollContainer as any)._autoScrollDragOverHandler
        );
        delete (scrollContainer as any)._autoScrollDragOverHandler;
      }
    }
  };

  const handleDragStart = () => {
    isDragging.value = true;
    nextTick(() => {
      startAutoScroll();
    });
  };

  const handleDragEnd = () => {
    isDragging.value = false;
    stopAutoScroll();
  };

  return {
    handleDragStart,
    handleDragEnd,
  };
}
